import type { Booking } from '@coachapp/shared';
import { CalendarDays, CheckCircle, Dumbbell, ExternalLink, MapPin, ShieldCheck, User as UserIcon, Video } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import CompleteBookingPrompt, {
  type CompleteBookingRequest,
} from '../components/CompleteBookingPrompt';
import ErrorAlert from '../components/ErrorAlert';
import { RootHeader } from '../components/RootHeader';
import TodayTasks from '../components/TodayTasks';
import TransitionLink from '../components/TransitionLink';
import WeekCalendar from '../components/WeekCalendar';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import type { BookingWithPlan } from '../lib/form-return';
import {
  datesWithBookings,
  formatIsoDate,
  getMonday,
  minutesUntilLabel,
  parseDateParam,
  readTodayView,
  splitDayBookings,
  TODAY_VIEW_STORAGE_KEY,
  relativeDay,
  shiftWeek,
  weekDates,
  type TodayView,
} from '../lib/today';
import {
  useGetBookingsQuery,
  useGetRetentionQuery,
  useGetTrainerProfileQuery,
  useUpdateBookingMutation,
} from '../store/coachApi';

const TodayPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useTransitionNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: bookings = [], error, isLoading, refetch } = useGetBookingsQuery();
  const { data: retention, error: retentionError } = useGetRetentionQuery();
  const { data: trainerProfile, error: profileError } = useGetTrainerProfileQuery();
  const [updateBooking] = useUpdateBookingMutation();
  const [view, setView] = useState<TodayView>(() => readTodayView(localStorage));

  const setTodayView = (next: TodayView) => {
    setView(next);
    localStorage.setItem(TODAY_VIEW_STORAGE_KEY, next);
  };
  const [actionError, setActionError] = useState<string | null>(null);
  const [completeRequest, setCompleteRequest] = useState<CompleteBookingRequest | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchError = error ? getApiErrorMessage(error, 'Не удалось загрузить расписание') : null;
  const retentionErrorMsg = retentionError
    ? getApiErrorMessage(retentionError, 'Не удалось загрузить данные')
    : null;
  const profileErrorMsg = profileError
    ? getApiErrorMessage(profileError, 'Не удалось загрузить данные')
    : null;

  const parseIso = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const now = new Date();
  const todayIso = formatIsoDate(now);
  const selected = parseDateParam(searchParams.get('date') ? `?date=${searchParams.get('date')}` : '', now);
  const isToday = selected === todayIso;

  const setSelected = (day: string) => {
    const params = new URLSearchParams(searchParams);
    if (day === formatIsoDate(new Date())) params.delete('date');
    else params.set('date', day);
    setSearchParams(params, { replace: true });
  };

  const days = weekDates(selected);
  const daysWithBookings = useMemo(() => datesWithBookings(bookings), [bookings]);
  const { hero, later, done } = useMemo(
    () => splitDayBookings(bookings, selected, new Date()),
    [bookings, selected],
  );

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  const formatCalendarDate = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
    });
  };

  const dayTitle = (() => {
    const relation = relativeDay(selected, now);
    if (relation === 'today') return t('Сегодня');
    if (relation === 'tomorrow') return t('Завтра');
    if (relation === 'yesterday') return t('Вчера');
    return formatCalendarDate(selected);
  })();

  const title =
    view === 'week'
      ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).formatRange(
          parseIso(weekDates(selected)[0]),
          parseIso(weekDates(selected)[6]),
        )
      : dayTitle;

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  const calculateEndTime = (startStr: string, duration: number) => {
    const end = new Date(new Date(startStr).getTime() + duration * 60000);
    return end.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  const typeLabel = (type: Booking['type']) =>
    type === 'GYM' ? t('В зале') : type === 'ONLINE' ? t('Онлайн') : t('Самостоятельно');

  const openEdit = (booking: Booking) => {
    navigate(`bookings/${booking.id}/edit`, { state: { booking } });
  };

  const openNewBooking = () => {
    navigate('/bookings/new', {
      state: isToday ? undefined : { startsAt: `${selected}T10:00` },
    });
  };

  const handleConfirm = async (booking: Booking) => {
    setUpdatingId(booking.id);
    setActionError(null);
    try {
      await updateBooking({ id: booking.id, data: { status: 'CONFIRMED' } }).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCompleteTap = (booking: Booking) => {
    if (booking.status === 'COMPLETED' || booking.status === 'CANCELLED') return;
    setCompleteRequest({
      bookingId: booking.id,
      clientId: booking.clientId,
      clientName: booking.client?.name,
    });
  };

  const renderStatusButton = (booking: Booking, small = false) => {
    if (booking.status !== 'PENDING' && booking.status !== 'CONFIRMED') return null;
    const isUpdating = updatingId === booking.id;
    const label = booking.status === 'PENDING' ? t('Подтвердить') : t('Завершить');
    const Icon = booking.status === 'PENDING' ? ShieldCheck : CheckCircle;
    return (
      <button
        type="button"
        disabled={isUpdating}
        onClick={(e) => {
          e.stopPropagation();
          if (booking.status === 'PENDING') void handleConfirm(booking);
          else handleCompleteTap(booking);
        }}
        className={`btn-secondary ${small ? 'px-3 py-1.5 text-xs' : 'w-full'}`}
      >
        <Icon size={15} className="shrink-0 text-primary" />
        {label}
      </button>
    );
  };

  const formatAtRiskReason = (reason: string) =>
    reason
      .split(',')
      .map((code) => t(`atRisk.${code.trim()}`))
      .join(' · ');

  const minutesUntil = hero ? minutesUntilLabel(selected, hero.startsAt, new Date()) : null;
  const isEmptyDay = !hero && later.length === 0 && done.length === 0;
  const allFinished = !hero && later.length === 0 && done.some((b) => b.status === 'COMPLETED');
  const scheduleMissing = Boolean(fetchError) && bookings.length === 0;

  return (
    <>
      <div className="page-container">
        <RootHeader
          title={title}
          trailing={
            <button
              type="button"
              aria-label={view === 'day' ? t('Неделя') : t('День')}
              onClick={() => setTodayView(view === 'day' ? 'week' : 'day')}
              className="btn-ghost"
            >
              <CalendarDays size={20} />
            </button>
          }
        />

        {fetchError && (
          <ErrorAlert message={fetchError} onRetry={() => void refetch()} onDismiss={() => {}} />
        )}
        {(actionError || retentionErrorMsg) && (
          <ErrorAlert
            message={actionError || retentionErrorMsg!}
            onDismiss={() => setActionError(null)}
          />
        )}
        {profileErrorMsg && <ErrorAlert message={profileErrorMsg} onDismiss={() => {}} />}

        {view === 'week' ? (
          <WeekCalendar
            bookings={bookings}
            workingHours={trainerProfile?.workingHours}
            weekStart={getMonday(parseIso(selected))}
            onWeekChange={(start) => setSelected(formatIsoDate(start))}
            onBookingClick={(booking) => navigate(`bookings/${booking.id}/edit`, { state: { booking } })}
            onSlotClick={(day, hour) => {
              const pad = (n: number) => n.toString().padStart(2, '0');
              const startsAt = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T${pad(hour)}:00`;
              navigate('bookings/new', { state: { startsAt } });
            }}
            onDayClick={(day) => {
              setSelected(formatIsoDate(day));
              setTodayView('day');
            }}
          />
        ) : (
          <>
        <div className="flex items-center gap-1 mb-6">
          <button
            type="button"
            aria-label={t('Предыдущая неделя')}
            onClick={() => setSelected(shiftWeek(selected, -1))}
            className="p-2 text-slate-400 hover:text-primary transition-colors"
          >
            ‹
          </button>
          <div className="flex-1 grid grid-cols-7 gap-1">
            {days.map((day) => {
              const date = new Date(`${day}T12:00:00`);
              const active = day === selected;
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={active}
                  aria-label={date.toLocaleDateString('ru-RU', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                  onClick={() => setSelected(day)}
                  className={`flex flex-col items-center py-2 rounded-xl text-xs font-bold transition-colors ${
                    active ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <span className="micro-label opacity-70">
                    {date.toLocaleDateString(locale, { weekday: 'short' })}
                  </span>
                  <span className="text-sm tabular-nums">
                    {date.toLocaleDateString(locale, { day: 'numeric' })}
                  </span>
                  <span
                    className={`mt-0.5 h-1 w-1 rounded-full ${
                      daysWithBookings.has(day)
                        ? active
                          ? 'bg-white'
                          : 'bg-primary'
                        : 'bg-transparent'
                    }`}
                  />
                </button>
              );
            })}
          </div>
          <button
            type="button"
            aria-label={t('Следующая неделя')}
            onClick={() => setSelected(shiftWeek(selected, 1))}
            className="p-2 text-slate-400 hover:text-primary transition-colors"
          >
            ›
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="spinner h-8 w-8" />
          </div>
        ) : scheduleMissing ? null : isEmptyDay ? (
          <div className="empty-state">
            <p className="font-medium max-w-sm">{t('Тренировок нет')}</p>
            <button type="button" onClick={openNewBooking} className="btn-primary mt-4">
              {t('Записать клиента')}
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {allFinished && (
              <div className="empty-state py-10" role="status">
                <CheckCircle className="text-emerald-500 mb-3" size={40} aria-hidden />
                <p className="text-lg font-bold text-slate-900">
                  {isToday ? t('На сегодня всё') : t('Все тренировки завершены')}
                </p>
                {isToday && (
                  <p className="mt-1 text-sm font-medium text-slate-500 max-w-xs">
                    {t('Молодец, все тренировки завершены')}
                  </p>
                )}
              </div>
            )}

            {hero && (
              <section className="card p-5 md:p-6">
                <div className="text-sm font-bold text-primary tabular-nums">
                  {formatTime(hero.startsAt)} – {calculateEndTime(hero.startsAt, hero.durationMinutes)}
                  {minutesUntil !== null && (
                    <span className="ml-2 font-semibold text-slate-500">
                      {t('через {{count}} мин', { count: minutesUntil })}
                    </span>
                  )}
                </div>
                <TransitionLink
                  to={`/clients/${hero.clientId}`}
                  className="mt-1 block text-xl font-bold text-slate-900 hover:text-primary transition-colors"
                >
                  {hero.client?.name || t('Клиент')}
                </TransitionLink>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-medium">
                  <span className="inline-flex items-center gap-1.5 text-slate-500">
                    {hero.type === 'GYM' ? (
                      <MapPin size={12} />
                    ) : hero.type === 'ONLINE' ? (
                      <Video size={12} />
                    ) : (
                      <UserIcon size={12} />
                    )}
                    {typeLabel(hero.type)}
                  </span>
                  {(hero as BookingWithPlan).trainingPlan?.name && (
                    <span className="inline-flex items-center gap-1.5 text-primary">
                      <Dumbbell size={12} />
                      {(hero as BookingWithPlan).trainingPlan!.name}
                    </span>
                  )}
                </div>
                {hero.type === 'ONLINE' && hero.link && (
                  <a
                    href={hero.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-primary-dark"
                  >
                    <ExternalLink size={12} />
                    {t('Открыть звонок')}
                  </a>
                )}
                <div className="mt-4">{renderStatusButton(hero)}</div>
              </section>
            )}

            {later.length > 0 && (
              <section className="space-y-3">
                <h2 className="section-label ml-2">{t('Дальше')}</h2>
                {later.map((b) => (
                  <article
                    key={b.id}
                    onClick={() => openEdit(b)}
                    className="card p-4 flex items-center gap-3 cursor-pointer hover:border-primary/40 transition-colors"
                  >
                    <span className="text-sm font-bold text-slate-900 tabular-nums shrink-0">
                      {formatTime(b.startsAt)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <TransitionLink
                        to={`/clients/${b.clientId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate font-bold text-slate-900 hover:text-primary transition-colors"
                      >
                        {b.client?.name || t('Клиент')}
                      </TransitionLink>
                      {(b as BookingWithPlan).trainingPlan?.name && (
                        <span className="block truncate text-xs font-medium text-slate-500">
                          {(b as BookingWithPlan).trainingPlan!.name}
                        </span>
                      )}
                    </span>
                    {renderStatusButton(b, true)}
                  </article>
                ))}
              </section>
            )}

            {done.length > 0 && (
              <section className="space-y-3">
                <button
                  type="button"
                  aria-expanded={showDone}
                  onClick={() => setShowDone((prev) => !prev)}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-slate-600 hover:text-primary transition-colors"
                >
                  {t('Завершённые ({{count}})', { count: done.length })}
                </button>
                {showDone &&
                  done.map((b) => (
                    <article
                      key={b.id}
                      onClick={() => openEdit(b)}
                      className="card p-4 flex items-center gap-3 cursor-pointer opacity-70 hover:border-primary/40 transition-colors"
                    >
                      <span className="text-sm font-bold text-slate-900 tabular-nums shrink-0">
                        {formatTime(b.startsAt)}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-bold text-slate-900">
                        {b.client?.name || t('Клиент')}
                      </span>
                    </article>
                  ))}
              </section>
            )}

            <button
              type="button"
              onClick={openNewBooking}
              className="block w-full text-center text-sm font-semibold text-primary py-2"
            >
              {t('Записать клиента')}
            </button>
          </div>
        )}
        {!isLoading && isToday && (retention?.atRiskClients.length ?? 0) > 0 && (
          <section className="space-y-3 mt-8">
            <h2 className="section-label ml-2">{t('Требуют внимания')}</h2>
            <ul className="space-y-3">
              {retention!.atRiskClients.slice(0, 3).map((c) => (
                <li key={c.id}>
                  <TransitionLink
                    to={`/clients/${c.id}`}
                    className="card p-4 block hover:border-primary/40 transition-colors"
                  >
                    <span className="block truncate font-bold text-slate-900">{c.name}</span>
                    <span className="block mt-0.5 text-xs text-slate-500">
                      {formatAtRiskReason(c.reason)}
                    </span>
                  </TransitionLink>
                </li>
              ))}
            </ul>
          </section>
        )}
        <TodayTasks selectedDate={selected} />
          </>
        )}
      </div>

      <CompleteBookingPrompt
        request={completeRequest}
        onClose={() => setCompleteRequest(null)}
        onSuccess={() => void refetch()}
      />
    </>
  );
};

export default TodayPage;
